'use client'

import React, { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'

export type AmbientMood = 'calm' | 'signal' | 'attention'

interface AmbientFieldProps {
  mood?: AmbientMood
  className?: string
}

/**
 * Member (dark) ambient field — soft mesh glow only, no grey fills.
 * Mood shifts accent hue toward live / attention tokens.
 */
export function AmbientField({ mood = 'calm', className }: AmbientFieldProps) {
  const [reduceMotion, setReduceMotion] = useState(false)

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReduceMotion(mq.matches)
    function handleChange() {
      setReduceMotion(mq.matches)
    }
    mq.addEventListener('change', handleChange)
    return () => mq.removeEventListener('change', handleChange)
  }, [])

  const position =
    mood === 'signal' ? '30% 25%' : mood === 'attention' ? '70% 35%' : '50% 40%'

  const mid =
    mood === 'signal'
      ? 'rgb(var(--state-live) / 0.22)'
      : mood === 'attention'
        ? 'rgb(var(--accent-to) / 0.2)'
        : 'rgb(var(--accent-from) / 0.14)'

  return (
    <div
      aria-hidden
      className={cn('pointer-events-none fixed inset-0 -z-10 overflow-hidden', className)}
    >
      <div
        className={cn('absolute inset-[-25%]', !reduceMotion && 'animate-ambient-drift')}
        style={{
          background: `radial-gradient(ellipse 75% 65% at ${position}, ${mid}, transparent 58%)`,
          filter: 'blur(56px)',
        }}
      />
      <div
        className="absolute inset-0 opacity-25 mix-blend-soft-light"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.45'/%3E%3C/svg%3E")`,
          backgroundSize: '160px 160px',
        }}
      />
    </div>
  )
}
